// src/pages/Signup.jsx
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { User, Mail, Lock, Eye } from "lucide-react";
import { supabase } from "../utils/supabaseClient";

const Signup = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirm: "",
  });
  const [agree, setAgree] = useState(false);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleEmailSignup = async (e) => {
    e.preventDefault();
    if (!agree) {
      alert("Please accept the Terms of Service and Privacy Policy to continue.");
      return;
    }
    if (form.password !== form.confirm) {
      alert("Passwords do not match.");
      return;
    }

    const { error } = await supabase.auth.signUp({
      email: form.email,
      password: form.password,
      options: {
        data: { full_name: form.name },
        emailRedirectTo: "https://papermind-ai.netlify.app/dashboard",
      },
    });
    
    if (error) {
      alert(error.message);
    } else {
      alert("Signup successful! Check your email for verification.");
      navigate("/login");
    }
  };

  const handleGoogleSignup = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: "https://papermind-ai.netlify.app/dashboard",
          queryParams: { prompt: "consent" },
        },
      });
      if (error) console.error("Google signup error:", error.message);
    } catch (err) {
      console.error("Google signup failed:", err);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white dark:from-gray-900 dark:to-gray-800 flex items-start justify-center py-16">
      <div className="w-full max-w-md px-6">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary-600 text-white font-bold mb-3">
            P
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Create Account</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Start analyzing research papers today
          </p>
        </div>

        <div className="card p-6 rounded-lg bg-white/5 dark:bg-gray-800 shadow">
          <form onSubmit={handleEmailSignup} className="space-y-4">
            <label className="block">
              <div className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                Full Name
              </div>
              <div className="relative">
                <input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  placeholder="John Doe"
                  className="w-full px-4 py-3 rounded-lg bg-gray-100 dark:bg-gray-700 placeholder-gray-400"
                />
                <User className="absolute right-3 top-3 w-5 h-5 text-gray-400" />
              </div>
            </label>

            <label className="block">
              <div className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                Email Address
              </div>
              <div className="relative">
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  placeholder="you@example.com"
                  className="w-full px-4 py-3 rounded-lg bg-gray-100 dark:bg-gray-700 placeholder-gray-400"
                />
                <Mail className="absolute right-3 top-3 w-5 h-5 text-gray-400" />
              </div>
            </label>

            <label className="block">
              <div className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                Password
              </div>
              <div className="relative">
                <input
                  name="password"
                  type="password"
                  value={form.password}
                  onChange={handleChange}
                  required
                  placeholder="Create a password"
                  className="w-full px-4 py-3 rounded-lg bg-gray-100 dark:bg-gray-700 placeholder-gray-400"
                />
                <Lock className="absolute right-9 top-3 w-5 h-5 text-gray-400" />
                <Eye className="absolute right-3 top-3 w-5 h-5 text-gray-400" />
              </div>
            </label>

            <label className="block">
              <div className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                Confirm Password
              </div>
              <div className="relative">
                <input
                  name="confirm"
                  type="password"
                  value={form.confirm}
                  onChange={handleChange}
                  required
                  placeholder="Confirm password"
                  className="w-full px-4 py-3 rounded-lg bg-gray-100 dark:bg-gray-700 placeholder-gray-400"
                />
                <Lock className="absolute right-3 top-3 w-5 h-5 text-gray-400" />
              </div>
            </label>

            <div className="flex items-start space-x-3">
              <input
                id="agree"
                type="checkbox"
                checked={agree}
                onChange={() => setAgree(!agree)}
                className="mt-1"
              />
              <label
                htmlFor="agree"
                className="text-sm text-gray-600 dark:text-gray-400"
              >
                I agree to the{" "}
                <Link to="/terms" className="text-primary-500 hover:underline">
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link to="/privacy" className="text-primary-500 hover:underline">
                  Privacy Policy
                </Link>
                .
              </label>
            </div>

            <button
              type="submit"
              className="w-full py-3 rounded-lg bg-primary-600 text-white font-medium"
            >
              Create Account
            </button>

            <div className="text-center text-sm text-gray-500">or</div>

            <button
              type="button"
              onClick={handleGoogleSignup}
              className="w-full inline-flex items-center justify-center gap-3 py-3 mt-2 rounded-lg border bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition"
            >
              <img
                src="https://www.svgrepo.com/show/355037/google.svg"
                alt="Google"
                className="w-5 h-5"
              />
              <span className="text-sm text-gray-700 dark:text-gray-200 font-medium">
                Sign up with Google
              </span>
            </button>
          </form>
        </div>

        <p className="text-center text-sm text-gray-500 mt-4">
          Already have an account?{" "}
          <Link to="/login" className="text-primary-500 hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Signup;
